import { Empty, Button, Space } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { semantic } from '@/theme/tokens';

export interface EmptyStateAction {
  label: string;
  /** Either invoke a callback or navigate to a path. `to` takes precedence. */
  onClick?: () => void;
  to?: string;
}

export interface EmptyStateProps {
  /** Localized heading. If omitted, falls back to `common:emptyState.title`. */
  title?: string;
  /** Optional description line. */
  description?: string;
  /**
   * Optional secondary hint shown in a quieter tone below the description —
   * useful for "this fills in once the wizard runs" kinds of explanations.
   */
  hint?: string;
  /** Primary action (CTA). */
  action?: EmptyStateAction;
  /** Optional secondary action — shown next to primary in a button row. */
  secondaryAction?: EmptyStateAction;
  /**
   * Optional custom illustration. Defaults to a brand-aligned leaf SVG.
   * Pass `false` to suppress the illustration entirely.
   */
  image?: ReactNode | false;
  /** Reduce vertical padding for embedded contexts (e.g., inside a tab). */
  compact?: boolean;
}

/**
 * Branded leaf illustration — soft Najdi palette, subtle enough to not
 * compete with the title but warmer than AntD's default greyscale clipart.
 */
function DefaultIllustration() {
  return (
    <svg
      width="80"
      height="64"
      viewBox="0 0 80 64"
      role="presentation"
      aria-hidden="true"
      style={{ marginInline: 'auto', display: 'block' }}
    >
      <ellipse cx="40" cy="56" rx="28" ry="3" fill="rgba(31, 77, 58, 0.08)" />
      <path
        d="M40 52 C 40 28, 22 20, 14 16 C 18 30, 26 44, 40 52 Z"
        fill={semantic.brand.primary}
        opacity="0.18"
      />
      <path
        d="M40 52 C 40 28, 58 20, 66 16 C 62 30, 54 44, 40 52 Z"
        fill={semantic.brand.accent}
        opacity="0.22"
      />
      <line
        x1="40"
        y1="52"
        x2="40"
        y2="20"
        stroke={semantic.brand.primary}
        strokeWidth="1.5"
        opacity="0.45"
      />
    </svg>
  );
}

/**
 * Defensive empty view.
 *
 * Every list/detail/tab that could receive zero rows MUST render this instead
 * of a blank area. Defaults are deliberately soft; pass a meaningful CTA via
 * `action` whenever the user can do something to populate the view.
 */
export default function EmptyState({
  title,
  description,
  hint,
  action,
  secondaryAction,
  image,
  compact,
}: EmptyStateProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const resolvedTitle = title ?? t('emptyState.title', { defaultValue: 'No data yet' });
  const resolvedDescription =
    description ??
    t('emptyState.description', { defaultValue: 'Once records exist, they show up here.' });

  const handleClick = (a: EmptyStateAction) => () => {
    if (a.to) navigate(a.to);
    else if (a.onClick) a.onClick();
  };

  const renderImage = () => {
    if (image === false) return <span style={{ display: 'none' }} />;
    if (image) return image;
    return <DefaultIllustration />;
  };

  return (
    <div style={{ padding: compact ? 24 : 48, textAlign: 'center' }}>
      <Empty
        image={renderImage()}
        imageStyle={{ height: image === false ? 0 : 64 }}
        description={
          <Space direction="vertical" size={6} style={{ maxWidth: 380, marginInline: 'auto' }}>
            <span style={{ fontSize: 14, color: 'rgba(0,0,0,0.88)', fontWeight: 500 }}>
              {resolvedTitle}
            </span>
            <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', lineHeight: 1.5 }}>
              {resolvedDescription}
            </span>
            {hint && (
              <span
                style={{
                  fontSize: 11,
                  color: 'rgba(0,0,0,0.4)',
                  fontStyle: 'italic',
                  lineHeight: 1.5,
                }}
              >
                {hint}
              </span>
            )}
          </Space>
        }
      >
        {(action || secondaryAction) && (
          <Space size={8} style={{ marginTop: 4 }}>
            {action && (
              <Button type="primary" onClick={handleClick(action)}>
                {action.label}
              </Button>
            )}
            {secondaryAction && (
              <Button onClick={handleClick(secondaryAction)}>{secondaryAction.label}</Button>
            )}
          </Space>
        )}
      </Empty>
    </div>
  );
}
